import { RootState } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import LocaleText from 'components/LocaleText'
import useLocaleText from 'components/useLocaleText'
import { agentLiterals, agentErrorText } from '../../model/literals'
import { formatLiteral } from 'utils/localeText'
import { classNames } from '../../styles/classNames'
import theme from '../../styles/theme.module.css'
import styles from './styles.module.css'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronUp, Clock, Plug, Plus, RefreshCw } from 'lucide-react'
import { AiOutlineWhatsApp } from 'react-icons/ai'
import instagramIcon from './icons/instagram.svg'
import gmailIcon from './icons/gmail.svg'
import calendarIcon from './icons/calendar.svg'
import slackIcon from './icons/slack.svg'
import { Button, Chip, CloseButton } from 'components/ui'
import { StoreeAvatar } from 'components/CommonComponents/StoreeAvatar'
import { useAgentContext } from 'contexts'
import { AgentModal } from 'components/Agent/components/AgentModal'
import { formatOffset } from 'components/Agent/model/scheduling'
import { useAgentNavigation } from 'components/Agent/hooks/useAgentNavigation'
import { getSidebarSchedule } from '../../model/sidebarSchedules'
import SidebarUserCard from '../../../Sidebar/SidebarUserCard'

const AgentTimezone = React.lazy(() =>
  import('../AgentTimezone').then(({ AgentTimezone }) => ({ default: AgentTimezone }))
)

const SCHEDULE_PREVIEW_COUNT = 3
const upcomingConnectors = [
  { name: 'Instagram', icon: instagramIcon },
  { name: 'Calendar', icon: calendarIcon },
  { name: 'Gmail', icon: gmailIcon },
  { name: 'Slack', icon: slackIcon },
]

interface IAgentSidebarProps {
  drawer?: boolean
  onNavigate?: () => void
  onClose?: () => void
}

export const AgentSidebar = ({ onNavigate, onClose, drawer = false }: IAgentSidebarProps) => {
  const labels = {
    ag_calendar: useLocaleText(agentLiterals.ag_calendar),
    ag_recurring: useLocaleText(agentLiterals.ag_recurring),
    ag_one_time: useLocaleText(agentLiterals.ag_one_time),
    back_button: useLocaleText(agentLiterals.back_button),
    ag_agent: useLocaleText(agentLiterals.ag_agent),
    ag_your_ai_ass: useLocaleText(agentLiterals.ag_your_ai_ass),
    close: useLocaleText(agentLiterals.close),
    scheduls: useLocaleText(agentLiterals.scheduls),
    schedule: useLocaleText(agentLiterals.schedule),
    noItems: useLocaleText(agentLiterals.noItems),
    local_auth_button: useLocaleText(agentLiterals.local_auth_button),
    ag_no_sch: useLocaleText(agentLiterals.ag_no_sch),
    ag_saving: useLocaleText(agentLiterals.ag_saving),
    ag_refresh: useLocaleText(agentLiterals.ag_refresh),
    see_less_sidebar: useLocaleText(agentLiterals.see_less_sidebar),
    see_more_sidebar: useLocaleText(agentLiterals.see_more_sidebar),
    ag_max_sch: useLocaleText(agentLiterals.ag_max_sch),
    ag_connectors: useLocaleText(agentLiterals.ag_connectors),
    ag_connector_soon: useLocaleText(agentLiterals.ag_connector_soon),
    ag_times_in_zone: useLocaleText(agentLiterals.ag_times_in_zone),
    ag_sch_tz: useLocaleText(agentLiterals.ag_sch_tz),
    ag_tz_desc: useLocaleText(agentLiterals.ag_tz_desc),
    ag_hourly: useLocaleText(agentLiterals.ag_hourly),
    t_daily: useLocaleText(agentLiterals.t_daily),
    t_weekly: useLocaleText(agentLiterals.t_weekly),
    t_monthly: useLocaleText(agentLiterals.t_monthly),
    ag_minute_number: useLocaleText(agentLiterals.ag_minute_number),
    ag_monthly_timing: useLocaleText(agentLiterals.ag_monthly_timing),
  }
  const locale = useSelector((state: RootState) => state.general.locale) || 'en'

  const { agentId, tzDelta, tzName, schedules, setWhatsappOpen } = useAgentContext()
  const { openSchedules } = useAgentNavigation()
  const navigate = useNavigate()

  const [timezone, setTimezone] = useState(false)
  const [schedulesOpen, setSchedulesOpen] = useState(true)
  const [schedulesExpanded, setSchedulesExpanded] = useState(false)
  const [connectorsExpanded, setConnectorsExpanded] = useState(true)

  const loading = !agentId || schedules.loading
  const rows = schedules.rows
    .map((row) => ({ ...row, display: getSidebarSchedule(row.schedule, tzDelta, labels, locale) }))
    .filter((row) => row.pending || row.error || row.display.due > Date.now())
    .sort(
      (a, b) =>
        Number(a.schedule.is_one_time) - Number(b.schedule.is_one_time) ||
        (a.schedule.is_one_time
          ? a.display.due - b.display.due
          : a.display.order - b.display.order || a.display.time - b.display.time)
    )
  const visibleSchedules = schedulesExpanded ? rows : rows.slice(0, SCHEDULE_PREVIEW_COUNT)
  const hiddenScheduleCount = Math.max(0, rows.length - SCHEDULE_PREVIEW_COUNT)
  const groups = [
    {
      label: labels.ag_recurring,
      recurring: true,
      rows: visibleSchedules.filter((row) => !row.schedule.is_one_time),
      count: rows.filter((row) => !row.schedule.is_one_time).length,
    },
    {
      label: labels.ag_one_time,
      recurring: false,
      rows: visibleSchedules.filter((row) => row.schedule.is_one_time),
      count: rows.filter((row) => row.schedule.is_one_time).length,
    },
  ]

  const canCreate = !!agentId && !schedules.loading && !schedules.error && schedules.rows.length < 20

  const showSchedules = (id?: string) => {
    setWhatsappOpen(false)
    openSchedules(id)
    onNavigate?.()
  }

  return (
    <aside className={classNames(styles.sidebar, theme.surface, drawer && styles.inDrawer)}>
      <div className={styles.sidebarHead}>
        <Button
          className={styles.sidebarBack}
          variant="icon"
          aria-label={labels.back_button}
          title={labels.back_button}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className={styles.backIcon} size={17} />
        </Button>
        <StoreeAvatar size={34} />
        <div className={styles.sidebarTitle}>
          <strong>{labels.ag_agent}</strong>
          <span>{labels.ag_your_ai_ass}</span>
        </div>
        {onClose && (
          <CloseButton
            className={styles.sidebarClose}
            aria-label={`${labels.close}: ${labels.ag_agent}`}
            onClick={onClose}
          />
        )}
      </div>
      <nav className={styles.sidebarNav} aria-label={labels.ag_agent}>
        <section className={styles.schedulesSection} aria-label={labels.scheduls}>
          <div className={styles.sectionHeading}>
            <h2>
              <Button
                variant="ghost"
                className={styles.schedulesToggle}
                aria-label={labels.scheduls}
                aria-expanded={schedulesOpen}
                aria-controls={drawer ? 'drawer-agent-schedule-content' : 'agent-schedule-content'}
                onClick={() => setSchedulesOpen((open) => !open)}
              >
                <Clock size={16} aria-hidden="true" />
                <span className={styles.sectionLabel}>{labels.scheduls}</span>
                <span className={styles.navCount} aria-hidden="true">
                  {loading && !rows.length ? (
                    <span className={styles.countSkeleton} />
                  ) : (
                    rows.length.toLocaleString(locale)
                  )}
                </span>
                <span className={styles.scheduleCreateSlot} aria-hidden="true" />
                {schedulesOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </Button>
            </h2>
            <Button
              variant="icon"
              className={styles.scheduleCreate}
              aria-label={labels.schedule}
              title={labels.schedule}
              isDisabled={!canCreate}
              onClick={() => showSchedules('new')}
            >
              <Plus size={17} />
            </Button>
          </div>
          <div hidden={!schedulesOpen} id={drawer ? 'drawer-agent-schedule-content' : 'agent-schedule-content'}>
            <div className={styles.sectionList} id={drawer ? 'drawer-agent-schedules' : 'agent-schedules'}>
              {loading && !schedules.rows.length && (
                <div role="status" aria-label={labels.noItems} className={styles.scheduleSkeletons}>
                  <span className={styles.skeletonCaption} />
                  {[0, 1, 2].map((index) => (
                    <div key={index} className={styles.skeletonRow} aria-hidden="true">
                      <span className={styles.skeletonTitle} />
                      <span className={styles.skeletonMeta} />
                    </div>
                  ))}
                </div>
              )}
              {schedules.error && (
                <div className={theme.error} role="alert">
                  <LocaleText {...agentErrorText(schedules.error)} />
                  <Button variant="ghost" onClick={() => void schedules.load()}>
                    {labels.local_auth_button}
                  </Button>
                </div>
              )}
              {!loading && !schedules.error && !rows.length && (
                <p className={styles.scheduleEmpty}>{labels.ag_no_sch}</p>
              )}
              {groups
                .filter((group) => group.rows.length)
                .map((group) => (
                  <div key={group.recurring ? 'recurring' : 'one-time'} className={styles.scheduleGroup}>
                    <h3 className={styles.groupCaption}>
                      {group.label} <span>{group.count.toLocaleString(locale)}</span>
                    </h3>
                    {group.rows.map(({ schedule, pending, error, display }) => (
                      <div key={schedule.id}>
                        <div className={styles.scheduleEntry}>
                          <Button
                            variant="ghost"
                            isDisabled={pending || !!error}
                            title={schedule.message}
                            onClick={() => showSchedules(schedule.id)}
                          >
                            <span className={styles.scheduleCopy}>
                              <span className={styles.scheduleTitle}>{schedule.message}</span>
                              <span className={styles.scheduleMeta}>
                                {display.label && (
                                  <Chip
                                    label={display.label}
                                    variant="outline"
                                    color="#1A1A17"
                                    bg="white"
                                    border="1px solid #E2E1DA"
                                    size="sm"
                                    radius="9px"
                                    className={styles.recurrenceTag}
                                    icon={<RefreshCw size={11} strokeWidth={1.6} />}
                                  />
                                )}
                                <span>{pending ? labels.ag_saving : display.timing}</span>
                              </span>
                            </span>
                          </Button>
                        </div>
                        {error && (
                          <div className={theme.error} role="alert">
                            <LocaleText {...agentErrorText(error)} />
                            <Button variant="ghost" onClick={() => void schedules.load()}>
                              {labels.ag_refresh}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
            {hiddenScheduleCount > 0 && (
              <Button
                variant="ghost"
                className={styles.showMore}
                aria-expanded={schedulesExpanded}
                aria-controls={drawer ? 'drawer-agent-schedules' : 'agent-schedules'}
                endIcon={schedulesExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                onClick={() => setSchedulesExpanded((expanded) => !expanded)}
              >
                {schedulesExpanded
                  ? labels.see_less_sidebar
                  : `${labels.see_more_sidebar} (${hiddenScheduleCount.toLocaleString(locale)})`}
              </Button>
            )}
            <div className={styles.sidebarScheduleFooter}>
              <Button
                className={styles.scheduleAdd}
                variant="primary"
                bg="#1E1E1E"
                _hover={{ bg: '#353530', transform: 'none', boxShadow: 'none' }}
                startIcon={<Plus size={15} />}
                isDisabled={!canCreate}
                onClick={() => showSchedules('new')}
              >
                {labels.schedule}
              </Button>
              {schedules.rows.length >= 20 && <p className={styles.sidebarMeta}>{labels.ag_max_sch}</p>}
            </div>
          </div>
        </section>
        <section aria-label={labels.ag_connectors}>
          <h2 className={styles.connectorsHeading}>
            <Button
              variant="ghost"
              className={styles.connectorsToggle}
              aria-expanded={connectorsExpanded}
              aria-controls={drawer ? 'drawer-agent-connectors' : 'agent-connectors'}
              startIcon={<Plug size={16} />}
              endIcon={connectorsExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              onClick={() => setConnectorsExpanded((expanded) => !expanded)}
            >
              <span className={styles.sectionLabel}>{labels.ag_connectors}</span>
            </Button>
          </h2>
          <div
            className={styles.connectorList}
            id={drawer ? 'drawer-agent-connectors' : 'agent-connectors'}
            hidden={!connectorsExpanded}
          >
            <Button
              variant="ghost"
              className={styles.connector}
              startIcon={<AiOutlineWhatsApp size={20} className={styles.whatsappIcon} />}
              onClick={() => {
                setWhatsappOpen(true)
                onNavigate?.()
              }}
            >
              WhatsApp
            </Button>
            {upcomingConnectors.map(({ name, icon }) => (
              <Button
                key={name}
                variant="ghost"
                className={styles.connector}
                isDisabled
                title={formatLiteral(labels.ag_connector_soon, {
                  name: name === 'Calendar' ? labels.ag_calendar : name,
                })}
                startIcon={<img src={icon} alt="" width={20} height={20} />}
              >
                {name === 'Calendar' ? labels.ag_calendar : name}
              </Button>
            ))}
          </div>
        </section>
      </nav>
      <Button
        variant="secondary"
        className={styles.timezoneCard}
        onClick={() => {
          setWhatsappOpen(false)
          setTimezone(true)
        }}
        aria-label={formatLiteral(labels.ag_times_in_zone, {
          timezone: tzName
            ? tzName.split('/').pop()!.replace(/_/g, ' ')
            : tzDelta == null
            ? labels.ag_sch_tz
            : formatOffset(tzDelta),
        })}
        startIcon={<Clock size={15} />}
      >
        <span className={styles.timezoneCopy}>
          <span className={styles.timezoneTitle}>
            {tzName
              ? tzName.split('/').pop()!.replace(/_/g, ' ')
              : tzDelta == null
              ? labels.ag_sch_tz
              : formatOffset(tzDelta)}
          </span>
          <span className={styles.timezoneHint}>{labels.ag_tz_desc}</span>
        </span>
      </Button>
      <div className={styles.sidebarAccount}>
        <SidebarUserCard />
      </div>
      {timezone && (
        <React.Suspense
          fallback={
            <AgentModal title={labels.ag_sch_tz} onClose={() => setTimezone(false)}>
              <p role="status">{labels.noItems}</p>
            </AgentModal>
          }
        >
          <AgentTimezone onClose={() => setTimezone(false)} />
        </React.Suspense>
      )}
    </aside>
  )
}
